import { useEffect, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getContactData,
  postContact,
  putContact,
} from "@services/API/Requests/ContactRequests";
import {
  deleteCardImage,
  uploadCardImage,
} from "@services/API/Requests/UserRequests";
import { getContactHistory } from "@services/API/Requests/ContactHistoryRequests";

import BlankUser from "@templates/BlankUserData";
import { USER } from "@types/user.ts";
import { USER_TYPE_STRING } from "@constants/Enums";
import { error_message, UserFieldErrorMessages } from "@constants/Errors";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { KAKELY_PATHS } from "@constants/NavigationPaths";
import { translatedDuplicateMessages } from "@constants/Errors";

export const useContactProfileForm = (id?: string) => {
  const navigate = useNavigate();

  const [contactData, setContactData] = useState<USER>();
  const [localContactData, setLocalContactData] = useState<USER>();
  const [contactHistoryData, setContactHistoryData] = useState();
  const [loading, setLoading] = useState(true);
  const [uploadCardsLoading, setUploadCardsLoading] = useState(false);
  const [contactHistoryLoading, setContactHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [dupErrorType, setDupErrorType] = useState<string>("");

  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [isExternalUser, setIsExternalUser] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [duplicateExistsModal, setDuplicateExistsModal] =
    useState<boolean>(false);
  const [duplicateUsers, setDuplicateUsers] = useState<USER[]>([]);

  const [dateCreated, setDateCreated] = useState("");
  const [implementerLogin, setImplementerLogin] = useState("");

  const [files, setFiles] = useState<
    { file: File; index: number; local: boolean }[]
  >([]);
  const [imageFileIndexes, setImageFileIndexes] = useState<number[]>([]);

  const [selectedOption, setSelectedOption] = useState("");

  const [createExternalContact] = useState(id === "createExternalContact");
  const [createInternalContact] = useState(id === "createInternalContact");

  const [errors, setErrors] = useState({
    name: false,
    first_name: false,
    last_name: false,
    kana_name: false,
    last_kana_name: false,
    first_kana_name: false,
    furigana_name: false,
    first_furigana_name: false,
    last_furigana_name: false,
    personal_tel: false,
    email: false,
    company_name: false,
    company_tel: false,
    company_fax: false,
    company_address: false,
    position_name: false,
    kana_name_length: false,
  });

  const errorToast = (msg: string) => toast.error(msg);
  const successToast = (msg: string) => toast.success(msg);

  useEffect(() => {
    if (
      contactData?.user_type === USER_TYPE_STRING.EXTERNAL ||
      createInternalContact
    ) {
      setIsExternalUser(true);
    }
  }, [contactData]);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await getContactData(
          setContactData,
          setLoading,
          setError,
          id,
          setLocalContactData,
        );
        setUserNotFound(response.status >= 300);
      } catch (error) {
        console.error(error_message.contact.data_load, error);
        setError(error as string);
      }
    };

    if (!createInternalContact && !createExternalContact) {
      fetchContactData();
      getContactHistory(
        setContactHistoryData,
        setContactHistoryLoading,
        setError,
        id,
      );
    } else {
      setLoading(false);
      setContactData(BlankUser);
    }
  }, [id]);

  const setBusinessCards = (cards: any[]) => {
    setContactData((prev) => ({ ...prev, card_image_urls: cards }));
  };

  const updateUserData = (key: keyof USER, value: string | boolean) => {
    setContactData((prev) => ({ ...prev!, [key]: value }));
  };

  const updateError = (field: string, value: boolean) => {
    setErrors((prev) => ({ ...prev, [field]: value }));
  };

  const handleDropdownChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(event.target.value);
    const index = event.target.selectedIndex - 1;
    setIsViewingHistory(index >= 0);
    setContactData(
      index >= 0 ? contactHistoryData[index]?.content?.new : localContactData,
    );
    if (index >= 0)
      setImplementerLogin(contactHistoryData[index]?.implementer_login);
  };

  const onCancel = () => {
    navigate(KAKELY_PATHS.main);
  };

  const handleOpenWarningModal = () => setWarningModalOpen(true);
  const handleCloseWarningModal = () => setWarningModalOpen(false);

  const handleCardUpload = async (contactId: number, init = false) => {
    try {
      const response = await uploadCardImage(contactId, files, init);
      if (response.status >= 300) errorToast(response.translatedMessage);
    } catch (err) {
      const msg = TranslateErrorMessage(err);
      errorToast(msg);
      console.error(error_message.image.upload, err);
    } finally {
      setFiles([]);
      setLoading(false);
    }
  };

  const handleDeleteCardImage = async (index: number) => {
    const response = await deleteCardImage(id, index, setLoading, setError);
    if (response.status >= 300) {
      const msg = TranslateErrorMessage(response.message);
      errorToast(msg);
      setError(msg);
    }
  };

  const handleCreateNewUser = async (e, forceCreate: boolean = false) => {
    e.preventDefault();
    setLoading(true);
    if (!contactData?.personal_tel && !contactData?.company_tel) {
      const msg = UserFieldErrorMessages.noPersonalOrCompanyTel;
      setError(msg);
      errorToast(msg);
      setLoading(false);
      return;
    }

    try {
      let userType;
      if (createExternalContact) {
        userType = USER_TYPE_STRING.EXTERNAL;
      } else {
        userType = USER_TYPE_STRING.KEYCLOAK_USER;
      }
      const response = await postContact(
        contactData,
        setLoading,
        setError,
        forceCreate,
        userType,
      );
      if (response.status === 200) {
        if (contactData?.card_image_urls?.newFiles?.length > 0) {
          setUploadCardsLoading(true);
          await handleCardUpload(response.user_id, true);
        }
        successToast(response.message);
        setBusinessCards(contactData?.card_image_urls);
        if (response.user_id) navigate(`/KAKELY/${response.user_id}`);
      } else {
        if (response.duplicate_users) {
          setError("");
          setDuplicateUsers(response.duplicate_users);
          setDuplicateExistsModal(true);
          setDupErrorType(translatedDuplicateMessages[response.type]);
        } else {
          const msg = TranslateErrorMessage(response.message);
          setError(msg);
          errorToast(msg);
        }
      }
    } catch (err) {
      const msg = TranslateErrorMessage(err.message);
      setError(msg);
      errorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (
    event,
    userId: string | undefined = id,
    forceCreate: boolean = true,
  ) => {
    event.preventDefault();
    setLoading(true);

    if (!contactData?.personal_tel && !contactData?.company_tel) {
      const msg = UserFieldErrorMessages.noPersonalOrCompanyTel;
      setError(msg);
      errorToast(msg);
      setLoading(false);
      return;
    }

    try {
      if (imageFileIndexes.length > 0) {
        await Promise.all(
          imageFileIndexes.map((index) => handleDeleteCardImage(index)),
        );
        setImageFileIndexes([]);
        setFiles((prev) =>
          prev.filter((file) => !imageFileIndexes.includes(file.index)),
        );
      }

      if (files.length > 0) {
        await handleCardUpload(userId);
      }

      const response = await putContact(
        contactData,
        undefined,
        undefined,
        userId,
        forceCreate,
      );
      if (response.status === 200) {
        successToast(response.message);

        if (createExternalContact || createInternalContact) {
          navigate(`/KAKELY/${userId}`);
        }

        await Promise.all([
          getContactData(
            setContactData,
            undefined,
            setError,
            userId,
            setLocalContactData,
          ),
          getContactHistory(
            setContactHistoryData,
            setContactHistoryLoading,
            setError,
            userId,
          ),
        ]);
      } else {
        const msg = TranslateErrorMessage(response.message);
        setError(msg);
        errorToast(msg);
      }
    } catch (err) {
      const msg = TranslateErrorMessage(err);
      setError(msg);
      errorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  return {
    contactData,
    localContactData,
    contactHistoryData,
    loading,
    uploadCardsLoading,
    contactHistoryLoading,
    error,
    isViewingHistory,
    isExternalUser,
    userNotFound,
    implementerLogin,
    warningModalOpen,
    errors,
    files,
    imageFileIndexes,
    selectedOption,
    createExternalContact,
    createInternalContact,
    dupErrorType,
    duplicateExistsModal,
    duplicateUsers,
    setFiles,
    setImageFileIndexes,
    setBusinessCards,
    updateUserData,
    updateError,
    handleDropdownChange,
    handleCreateNewUser,
    handleUpdateUser,
    onCancel,
    handleOpenWarningModal,
    handleCloseWarningModal,
    setDuplicateExistsModal,
  };
};
